import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ChartLine, Crown, Users, Star, Trophy, ArrowRight } from "@phosphor-icons/react";

export default function Analytics() {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/analytics/host")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 403 ? "pro_required" : "fetch"));
  }, []);

  if (error === "pro_required") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="analytics-pro-gate">
          <Crown weight="duotone" size={36} color="#FFCC00" className="mx-auto" />
          <h1 className="font-display text-4xl tracking-tight mt-4">
            {lang === "da" ? "ANALYTICS ER EN PRO-FORDEL." : "ANALYTICS IS A PRO PERK."}
          </h1>
          <p className="text-zinc-400 mt-2">
            {lang === "da" ? "Bliv Pro Crew for at se deltagelse, ratings og fill rate på tværs af dine sessioner." : "Become Pro Crew to see participation, ratings, and fill rate across your sessions."}
          </p>
          <Link to="/pro" data-testid="analytics-upgrade-cta" className="kinetic-btn inline-flex items-center gap-2 mt-6 bg-[#FFCC00] hover:bg-[#e6b800] text-black px-5 py-3 font-bold uppercase tracking-widest text-sm">
            <Crown weight="fill" size={16} /> {lang === "da" ? "Bliv Pro" : "Upgrade to Pro"} <ArrowRight weight="bold" size={14} />
          </Link>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <AppHeader />
        <div className="max-w-7xl mx-auto px-6 py-20 label-overline">{lang === "da" ? "Indlæser…" : "Loading…"}</div>
      </div>
    );
  }

  const sportLabels = { cycling: "Cycling", running: "Running", padel: "Padel" };
  const maxSport = Math.max(1, ...Object.values(data.sessions_by_sport || {}));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2">
          <ChartLine weight="duotone" size={22} color="#FFCC00" />
          <div className="label-overline" style={{ color: "#FFCC00" }}>{lang === "da" ? "Pro analytics" : "Pro analytics"}</div>
        </div>
        <h1 className="font-display text-5xl tracking-tight mt-2">
          {lang === "da" ? "DIT VÆRTS-DASHBOARD." : "YOUR HOST DASHBOARD."}
        </h1>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="analytics-stats">
          <Kpi icon={<Trophy weight="duotone" size={18} color="#007AFF" />} label={lang === "da" ? "Sessioner" : "Sessions"} value={data.total_sessions} />
          <Kpi icon={<Users weight="duotone" size={18} color="#34C759" />} label={lang === "da" ? "Deltagere total" : "Total participants"} value={data.total_participants} />
          <Kpi icon={<ChartLine weight="duotone" size={18} color="#FF9500" />} label={lang === "da" ? "Fill rate" : "Fill rate"} value={`${data.fill_rate_pct}%`} />
          <Kpi icon={<Star weight="duotone" size={18} color="#FFCC00" />} label={lang === "da" ? "Gennemsnitlig rating" : "Avg rating"} value={data.average_rating ? `${data.average_rating} ★` : "—"} />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div className="tf-card p-6">
            <h2 className="font-display text-2xl tracking-tight">{lang === "da" ? "Sessioner pr. sport" : "Sessions per sport"}</h2>
            <div className="space-y-4 mt-5">
              {Object.entries(data.sessions_by_sport || {}).map(([sport, count]) => (
                <div key={sport}>
                  <div className="flex items-center justify-between text-xs uppercase tracking-widest mb-1">
                    <span>{sportLabels[sport] || sport}</span>
                    <span className="font-mono-tag text-zinc-300">{count}</span>
                  </div>
                  <div className="h-2 bg-[#262626]">
                    <div className="h-full bg-[#007AFF]" style={{ width: `${(count / maxSport) * 100}%` }} />
                  </div>
                </div>
              ))}
              {Object.keys(data.sessions_by_sport || {}).length === 0 && (
                <div className="text-sm text-zinc-500">{lang === "da" ? "Ingen data endnu. Opret en session for at fylde dette ud." : "No data yet. Host a session to fill this in."}</div>
              )}
            </div>
          </div>

          <div className="tf-card p-6">
            <h2 className="font-display text-2xl tracking-tight">{lang === "da" ? "Aktivitet" : "Activity"}</h2>
            <div className="mt-5 space-y-3 text-sm">
              <Row label={lang === "da" ? "Gennemførte sessioner" : "Completed sessions"} value={data.completed_sessions} />
              <Row label={lang === "da" ? "På venteliste total" : "Waitlist total"} value={data.total_waitlist} />
              <Row label={lang === "da" ? "Antal ratings modtaget" : "Ratings received"} value={data.ratings_count} />
            </div>
            <button onClick={() => nav("/create")} className="kinetic-btn mt-6 w-full bg-[#007AFF] hover:bg-[#0a66d0] text-white px-4 py-3 font-bold uppercase tracking-widest text-sm">
              {lang === "da" ? "Opret en ny session" : "Host another session"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Kpi({ icon, label, value }) {
  return (
    <div className="tf-card p-5">
      <div className="flex items-center gap-2">{icon}<span className="label-overline">{label}</span></div>
      <div className="font-display text-4xl mt-2 leading-none">{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-[#262626] pb-2 last:border-0">
      <span className="text-zinc-300">{label}</span>
      <span className="font-mono-tag text-white">{value}</span>
    </div>
  );
}
